/**
 * 外注見積の一覧（#198、docs/79）の小さな部品：状態のチップ・読み取りの列・資料のアイコン・要確認の理由の文。
 *   色だけで伝えない（チップの文字としるし ⚠・✓）。緑・黄は使わない（docs/10 §4.1）。言葉は t() を通す
 */
import {
  VENDOR_QUOTE_DOC_KIND_LABEL,
  VENDOR_QUOTE_LIST_MSG,
  vendorQuoteReasonTexts,
  vendorQuoteStateLabel,
  type VendorQuoteDocKind,
  type VendorQuoteRow,
} from "@genbaryoku/contract";
import type { QueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { t } from "../../labels.js";

import { ymdHmShort } from "../../lib/dates.js";
/** 一覧の問い合わせ（キーの頭 vql）・ホームを読み直す */
export const invalidateList = (qc: QueryClient) => {
  void qc.invalidateQueries({ queryKey: ["vql"] });
  void qc.invalidateQueries({ queryKey: ["vql-filters"] });
  void qc.invalidateQueries({ queryKey: ["home"] });
};

/** 取込日：今年は「10/17」、去年より前は「25/10/17」（#320 ★8＝年2桁） */
export function receivedLabel(iso: string): string {
  const d = new Date(iso);
  const md = `${d.getMonth() + 1}/${d.getDate()}`;
  return d.getFullYear() === new Date().getFullYear() ? md : `${String(d.getFullYear() % 100).padStart(2, "0")}/${md}`;
}
/** 「26/10/17 09:05」（#320 ★8） */
export function receivedTitle(iso: string): string {
  return ymdHmShort(iso);
}
export const yenOrDash = (n: number | null): string => (n == null ? "—" : n.toLocaleString("ja-JP"));

/** 要確認・不採用の理由の文（パネルの帯・チップの title） */
export function reasonText(r: VendorQuoteRow): string {
  if (r.state === "rejected" && r.rejected) {
    const d = new Date(r.rejected.at);
    return `不採用 ${d.getMonth() + 1}/${d.getDate()}${r.rejected.reason ? `（${t(r.rejected.reason)}）` : ""}`;
  }
  if (r.state === "review") return vendorQuoteReasonTexts(r.reasons, r.checkNote).map((x) => t(x)).join("。");
  if (r.state === "used" && r.partial) return `残り ${r.usage.all - r.usage.done}行`;
  return "";
}

const SVG = (d: ReactNode) => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    {d}
  </svg>
);
export const ICON = {
  warn: SVG(<path d="M12 3l9.5 17h-19zM12 10v4.5M12 17.6v.1" />),
  check: SVG(<path d="M5 12.5l4.5 4.5L19 7" />),
  half: SVG(
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="M12 4a8 8 0 0 1 0 16z" fill="currentColor" />
    </>,
  ),
  scan: SVG(
    <>
      <path d="M7 3h7l4 4v14H7z" />
      <path className="vql-scanline" d="M5 12h14" />
    </>,
  ),
  dot: SVG(<circle cx="12" cy="12" r="7" strokeDasharray="3 3" />),
  pdf: SVG(
    <>
      <path d="M6 2.5h8.5l4 4V21.5H6z" />
      <path d="M14.5 2.5v4h4" />
      <text x="12.2" y="17.2" textAnchor="middle" fontSize="5.6" fontWeight="700" fill="currentColor" stroke="none" fontFamily="sans-serif">PDF</text>
    </>,
  ),
  image: SVG(
    <>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <circle cx="9" cy="10" r="1.8" />
      <path d="M21 16l-5-5-9 8" />
    </>,
  ),
  fax: SVG(
    <>
      <path d="M7 9V3h8l2 2v4" />
      <rect x="3" y="9" width="18" height="9" rx="2" />
      <path d="M7 18v3h10v-3M15 13h2" />
    </>,
  ),
  chev: SVG(<path d="M9 6l6 6-6 6" />),
  full: SVG(<path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" />),
  shrink: SVG(<path d="M9 4v5H4M15 4v5h5M9 20v-5H4M15 20v-5h5" />),
  close: SVG(<path d="M6 6l12 12M18 6L6 18" />),
  filter: SVG(<path d="M4 6h16M7 12h10M10 18h4" />),
  search: SVG(
    <>
      <circle cx="11" cy="11" r="6.5" />
      <path d="M16 16l4.5 4.5" />
    </>,
  ),
};

/** 状態のチップ（一覧・カード・パネル）。AI読み取り中は走査線が動く（動きを減らす設定では止まる） */
export function StateChip({ r, sub = true }: { r: VendorQuoteRow; sub?: boolean }) {
  const icon = r.state === "reading" ? (r.waiting ? ICON.dot : ICON.scan) : r.state === "review" ? ICON.warn : r.state === "used" ? (r.partial ? ICON.half : ICON.check) : null;
  const why = reasonText(r);
  return (
    <>
      <span className={`vql-chip is-${r.state}`} title={why || undefined} data-testid="vql-state">
        {icon}
        {vendorQuoteStateLabel(r.state, r.partial)}
      </span>
      {sub && r.state === "reading" && r.waiting ? <span className="vql-chipsub">{VENDOR_QUOTE_LIST_MSG.waiting}</span> : null}
    </>
  );
}

/** 読み取りの列：✓ 検算一致／⚠ 要確認／読み取れず／確認済み／— */
export function ReadCell({ r }: { r: VendorQuoteRow }) {
  if (r.check === "failed") return <span className="vql-readng">{ICON.warn}{VENDOR_QUOTE_LIST_MSG.readFailed}</span>;
  if (r.check === "mismatch" && !r.checkAcked) return <span className="vql-readng" title={r.checkNote ?? undefined}>{ICON.warn}{VENDOR_QUOTE_LIST_MSG.readNg}</span>;
  if (r.check === "mismatch") return <span className="vql-readok" title={r.checkNote ?? undefined}>{ICON.check}{VENDOR_QUOTE_LIST_MSG.readAcked}</span>;
  if (r.check === "ok") return <span className="vql-readok">{ICON.check}{VENDOR_QUOTE_LIST_MSG.readOk}</span>;
  return <span className="muted">—</span>;
}

export const docIcon = (k: VendorQuoteDocKind) => ICON[k];
export const docTitle = (r: VendorQuoteRow) => `${VENDOR_QUOTE_LIST_MSG.original}（${VENDOR_QUOTE_DOC_KIND_LABEL[r.source]}${r.pageCount ? `・${r.pageCount}ページ` : ""}）`;
/** 削除の窓（一覧のパネルと中身の画面で同じ窓・言葉。docs/79 §11.1 ク）。題は「削除」・脇は「取消」 */
export function VendorQuoteDeleteDialog({ name, busy, onCancel, onOk }: { name: string; busy: boolean; onCancel: () => void; onOk: () => void }) {
  return (
    <div className="dim" onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div className="dlg vql-dlg" role="dialog" aria-label={VENDOR_QUOTE_LIST_MSG.deleteTitle} data-testid="vql-delete-dialog" onKeyDown={(e) => e.key === "Escape" && onCancel()}>
        <div className="dh">{VENDOR_QUOTE_LIST_MSG.deleteTitle}</div>
        <div className="db">
          <p>{VENDOR_QUOTE_LIST_MSG.deleteBody.replace("{name}", name)}</p>
        </div>
        <div className="df">
          <button type="button" className="btn quiet" autoFocus onClick={onCancel}>取消</button>
          <button type="button" className="btn shu" disabled={busy} onClick={onOk} data-testid="vql-delete-ok">削除</button>
        </div>
      </div>
    </div>
  );
}

/** 件名（空なら原本のファイル名） */
export const titleOf = (r: VendorQuoteRow): string => r.title || r.fileName || "—";
